import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { THUMB_STATUS } from '@src/constants/timeline';
import { WAVEFORM_STATUS } from '@src/constants/project';
import { getSourceUrl, isVideoSource } from '@src/services/project';
import { fetchSourcePeaks } from '@src/services/sourceWaveform';
import createFifoCache from '@src/services/fifoCache';
import createPoller from '@src/utils/poller';
import apis from '@src/apis';
import useAudioWaveform from './useAudioWaveform';

// Parsed BE peaks kept by url so remounting the track (collapse/expand the
// timeline) doesn't refetch. Full-res peaks can be large (≈ peaksPerSecond ×
// duration), so keep only a few — the media track has a single source anyway.
const peaksCache = createFifoCache(4);

// Stop waiting for the BE waveform after this, then decode instead. Bounds the
// poll for a waveform that never completes.
const WAVEFORM_POLL_TIMEOUT_MS = 5 * 60 * 1000;

const idle = (status) => ({ status, peaks: [] });

const readyUrlOf = (source) => {
  const response = source?.waveform?.response;
  return response?.status === WAVEFORM_STATUS.SUCCESS
    ? response?.metadata?.url || null
    : null;
};

// The BE waveform is worth waiting for (show a skeleton, don't decode) only for a
// video source that hasn't already failed. Anything else — a non-video source, a
// FAILURE — goes straight to the in-browser decode.
const waitsForBe = (source) =>
  isVideoSource(source) &&
  source?.waveform?.response?.status !== WAVEFORM_STATUS.FAILURE;

/**
 * Waveform for the source. Prefers the BE-generated peaks file (a cheap fetch —
 * no whole-video download, no Web Audio decode). While the BE waveform is still
 * being generated it shows a skeleton — it does NOT decode in parallel — and
 * only falls back to the in-browser decode when the BE waveform won't come (a
 * non-video source, a FAILURE, or the poll gives up). Polls source preparation
 * and swaps in the peaks once ready.
 *
 * Returns `{ status, peaks }` like useAudioWaveform, so the track is unchanged.
 */
const useSourceWaveform = (source, opts) => {
  const projectId = useSelector((state) => state.project.projectId);
  const videoUrl = getSourceUrl(source);

  // BE peaks url: from the loaded project, else picked up by the poll below.
  const [polledUrl, setPolledUrl] = useState(null);
  const beUrl = readyUrlOf(source) || polledUrl;

  // Falls back to the in-browser decode once the BE waveform won't arrive.
  const [decode, setDecode] = useState(() => !waitsForBe(source));

  // Still generating: poll source preparation. SUCCESS gives the url; FAILURE or
  // timeout gives up and decodes. Not run once we've decided to decode.
  useEffect(() => {
    if (beUrl || decode || !projectId) return undefined;
    const controller = new AbortController();
    createPoller(() => apis.project.getSourcePreparationProgress(projectId), {
      isSuccess: (r) => r?.result?.waveformStatus === WAVEFORM_STATUS.SUCCESS,
      isFailure: (r) => r?.result?.waveformStatus === WAVEFORM_STATUS.FAILURE,
      signal: controller.signal,
      timeout: WAVEFORM_POLL_TIMEOUT_MS,
    })
      .then((r) => setPolledUrl(r?.result?.waveformMetadata?.url || null))
      .catch((err) => {
        if (err.name !== 'AbortError') setDecode(true);
      });
    return () => controller.abort();
  }, [beUrl, decode, projectId]);

  // Fetch + parse the BE peaks once per url, cached.
  const [be, setBe] = useState(() => {
    const cached = beUrl && peaksCache.get(beUrl);
    return cached
      ? { status: THUMB_STATUS.READY, peaks: cached }
      : idle(THUMB_STATUS.LOADING);
  });
  const cancelledRef = useRef(false);
  useEffect(() => {
    cancelledRef.current = false;
    if (!beUrl) return undefined;
    const cached = peaksCache.get(beUrl);
    if (cached) {
      setBe({ status: THUMB_STATUS.READY, peaks: cached });
      return undefined;
    }
    setBe(idle(THUMB_STATUS.LOADING));
    fetchSourcePeaks(beUrl).then((peaks) => {
      if (cancelledRef.current) return;
      if (!peaks) {
        setBe(idle(THUMB_STATUS.FAILED));
        return;
      }
      peaksCache.set(beUrl, peaks);
      setBe({ status: THUMB_STATUS.READY, peaks });
    });
    return () => {
      cancelledRef.current = true;
    };
  }, [beUrl]);

  const beActive = !!beUrl && be.status !== THUMB_STATUS.FAILED;
  // Decode once we've given up on the BE waveform, or its peaks file wouldn't load.
  const decodeNow = decode || be.status === THUMB_STATUS.FAILED;
  const fallback = useAudioWaveform(videoUrl, opts, decodeNow);

  if (beActive) return be;
  if (decodeNow) return fallback;
  return idle(THUMB_STATUS.LOADING); // skeleton while the BE waveform generates
};

export default useSourceWaveform;
